import { handled, toCollect, type Stop } from '@day';
import { current, keyOf, routeTitle, type Model, type Person } from '../data/model';
import { signOut } from '../auth/session';
import { toast } from '../ui/Overlays';
import { copyText, trackingLink } from '../pages/StopBody';
import { routeOfPerson } from '../pages/Live';
import { printRoute } from '../pages/RouteSheet';
import * as act from './actions';
import { openDialog } from './dialogs';
import { settle } from './hold';
import { deleteKey } from './platform';
import { closeMenu, menus, type Item } from './menu';
import { backToDay, besideMap, closestTo, discardPlan, discardShare, drawZone, frame, go, hide, openCourier, openPerson, openRoute, openShare, openStop, openWaiting, openZones, planWith, setGroup, setMap, setShare, theme, ui } from './ui';
import { mostOf, sharedNow } from './sharing';
import { loadOfAll } from '../data/runs';
import { leftWords } from '../pages/Share';
import { addDays, dayOf } from '@day';
import { dayLabel, fromLabel } from '../pages/Waiting';
import { dayWords, plural, streetOf, time, two, VEHICLES, vehicleWords } from './words';
import { routeDay } from '../data/routesPage';

/**
 * Every menu, from right-click or ⋯ (control-room-web.html: stopMenu, routeMenu, courierMenu, lookMenu, colMenu,
 * bulkMenu, spotMenu), fitted to the redrawn rows and panes. ⌘K finds the same items. What the rules don't let an owner
 * do (change a route a courier has started) shows greyed with the reason, never missing without one.
 */

const m = () => current.m!;
const av = (p: Person | null) => (p ? { hex: p.hex, on: p.on, first: p.first, photo: p.courier.photoUrl } : undefined);
/** A route waiting to start: its stops and order change from here (act.cantChange says why others don't). */
const changeable = (mm: Model, routeId: string) => mm.route(routeId)?.state === 'waiting';
/** Why a declined route's stops stay put, as the menus say it. */
const declinedWhy = (mm: Model, routeId: string) => (mm.route(routeId)?.state === 'declined' ? act.cantChange(mm, mm.raw(routeId)) : null);

/** Routes a stop can move to: the routes not started (a started route belongs to its courier). */
export function moveItems(keys: string[], except?: string): Item[] {
  const mm = m();
  const to = mm.routes.filter((r) => r.state === 'waiting' && r.id !== except);
  return to.map((r, i) => ({
    label: routeTitle(r, mm.zone),
    value: mm.person(r.riderId)?.first,
    avatar: av(mm.person(r.riderId)),
    key: i < 9 ? String(i + 1) : undefined,
    run: () => act.moveStops(keys, r.id),
  }));
}

const call = (label: string, tel?: string | null): Item[] => (tel ? [{ label, icon: 'phone', run: () => void (location.href = `tel:${tel.replace(/\s/g, '')}`) }] : []);

/** A stop the business can plan on a new route: off a route not started, or tried and not delivered. */
export function planStopFrom(mm: Model, s: Stop): act.PlanStop | null {
  const stored = act.stopForSending(mm, s.routeId, s.id);
  if (!stored) return null;
  const raw = mm.raw(s.routeId);
  return {
    id: crypto.randomUUID(),
    address: s.address,
    area: s.area,
    lat: Number(stored.lat),
    lng: Number(stored.lng),
    name: s.name,
    phone: s.phone,
    from: stored,
    fromKey: keyOf(s),
    googleUntil: raw && typeof stored.lat === 'number' && s.spot && !s.rough ? (raw.googleUntil ? Number((raw.googleUntil as { toMillis?: () => number }).toMillis?.() ?? 0) || null : null) : null,
  };
}

export function stopMenu(key: string): Item[] {
  const mm = m();
  const s = mm.stop(key);
  if (!s) return [];
  const r = mm.route(s.routeId);
  const p = mm.person(s.riderId);
  const movable = changeable(mm, s.routeId);
  const declined = declinedWhy(mm, s.routeId);
  const link = s.trackingCode && !s.handover ? trackingLink(s.trackingCode) : null;
  const theirs = `${p?.first ?? 'The courier'} has started this route, so it changes on their phone`;
  const items: Item[] = [
    { hd: `${two(s.n)} · ${streetOf(s)}` },
    { label: 'Open the stop', icon: 'notePencil', key: '↵', run: () => openStop(key) },
    { label: 'Show on the map', icon: 'mapTrifold', key: 'M', run: () => showOnMap(key) },
  ];
  if (!s.handover) {
    if (link) items.push({ label: 'Copy tracking link', icon: 'link', key: 'C', run: () => copyText(link, 'Tracking link copied') });
    else items.push({ label: 'Copy tracking link', icon: 'link', off: true }, { note: r?.state === 'waiting' ? `The link is made when ${p?.first ?? 'the courier'} starts the route` : 'No tracking link on this stop' });
  }
  if (link && (s.state === 'current' || s.state === 'ahead')) items.push({ label: 'Send the customer the link', icon: 'paperPlaneTilt', key: 'S', run: () => act.sendLink(s) });
  items.push({ label: 'Copy the address', icon: 'copy', run: () => copyText(s.writtenAddress ?? s.address, 'Address copied') }, ...call(`Call ${s.name?.split(/\s+/)[0] ?? 'the customer'}`, s.phone));
  items.push({ sep: true });
  if (movable) {
    items.push({
      label: 'Move to',
      icon: 'arrowsClockwise',
      value: r ? routeTitle(r, mm.zone) : undefined,
      sub: () => moveItems([key], s.routeId),
      search: true,
      empty: () => [
        { label: 'Show every route', icon: 'x' },
        { label: 'Plan a new route with it', icon: 'path', run: () => newRouteWith([s]) },
      ],
    });
    items.push({ label: 'Plan a new route with it', icon: 'path', key: 'P', run: () => newRouteWith([s]) });
  } else if (declined) {
    items.push({ label: 'Move to', icon: 'arrowsClockwise', off: true }, { note: declined });
  } else if (!handled(s) && r?.state === 'running') {
    items.push({ label: 'Move to', icon: 'arrowsClockwise', off: true }, { note: theirs });
  }
  const out = s.state === 'failed' ? act.retryOut(mm, s) : null;
  if (s.state === 'failed' && s.reason !== 'anotherDay' && r?.state === 'running') {
    if (out) items.push({ label: 'Try again', icon: 'arrowsClockwise', off: true }, { note: `Already sent to ${mm.person(out.riderId)?.first ?? 'a courier'}` });
    else items.push({ label: 'Try again', icon: 'arrowsClockwise', key: 'T', run: () => openDialog({ kind: 'tryAgain', key }) });
  }
  if (s.state === 'failed' && (s.reason === 'anotherDay' || r?.state !== 'running')) items.push({ label: s.reason === 'anotherDay' ? 'Plan it on a new route' : 'Send it on a new route', icon: 'calendarBlank', key: 'P', run: () => newRouteWith([s]) });
  if (s.state === 'left') items.push({ label: 'Send it on a new route', icon: 'path', key: 'P', run: () => newRouteWith([s]) });
  items.push({ sep: true });
  if (movable) items.push({ label: 'Take off the route', icon: 'trash', key: deleteKey, danger: true, run: () => act.takeOffStops([key]) });
  else if (declined) items.push({ label: 'Take off the route', icon: 'trash', off: true, danger: true }, { note: declined });
  else if (!handled(s) && r?.state === 'running') items.push({ label: 'Take off the route', icon: 'trash', off: true, danger: true }, { note: theirs });
  return items;
}

export function routeMenu(id: string): Item[] {
  const mm = m();
  const r = mm.route(id);
  if (!r) return [];
  const p = mm.person(r.riderId);
  const hidden = ui.get().hidden.has(id);
  const waiting = r.state === 'waiting';
  const offerable = r.state === 'waiting' || r.state === 'declined';
  const links = r.stops.filter((s) => s.trackingCode && !s.handover);
  const items: Item[] = [
    { hd: `${p?.first ?? 'A courier'} · ${routeTitle(r, mm.zone)}` },
    { label: 'Open the route', icon: 'path', key: '↵', run: () => openRoute(id) },
    {
      label: 'Fit the map to it',
      icon: 'arrowsOut',
      key: 'M',
      run: () => {
        if (hidden) hide(id);
        if (!besideMap(ui.get())) setMap(true);
        openRoute(id);
      },
    },
    { label: hidden ? 'Show on the map' : 'Hide from the map', icon: hidden ? 'eye' : 'eyeSlash', key: 'H', run: () => hide(id) },
    { sep: true },
  ];
  if (waiting) {
    items.push({ label: 'Sort again', icon: 'path', key: 'S', off: r.stops.length < 2, run: () => void act.sortSent(id) });
    items.push({ label: 'Reverse the order', icon: 'arrowsDownUp', key: 'R', off: r.stops.length < 2, run: () => act.reverse(id) });
    items.push({ note: 'Or drag a stop in the list to change the order.' });
  } else if (r.state === 'declined') {
    items.push({ label: 'Reverse the order', icon: 'arrowsDownUp', off: true }, { note: act.cantChange(mm, mm.raw(id)) ?? '' });
  } else if (r.state === 'running') {
    items.push({ label: 'Reverse the order', icon: 'arrowsDownUp', off: true }, { note: `${p?.first ?? 'The courier'} has started it: the order is theirs now` });
  }
  if (links.length) items.push({ label: `Copy ${plural(links.length, 'tracking link')}`, icon: 'link', key: 'C', run: () => copyText(links.map((s) => `${streetOf(s)}: ${trackingLink(s.trackingCode!)}`).join('\n'), `${plural(links.length, 'tracking link')} copied`) });
  items.push(...call(`Call ${p?.first ?? 'the courier'}`, p?.courier.phone));
  if (r.state === 'running' && mm.looks.some((x) => x.kind === 'stillOpen' && x.route?.id === id)) items.push({ label: `Remind ${p?.first ?? 'them'} to end it`, icon: 'bell', run: () => act.remindCourier(id) });
  items.push({ label: 'Copy to a new route', icon: 'copy', run: () => void act.copyRoute(id) });
  items.push({ label: 'Print the route sheet', icon: 'printer', key: 'P', run: () => printRoute(id) });
  if (offerable) {
    items.push(
      { sep: true },
      { label: r.state === 'declined' ? 'Send it to someone' : 'Give it to someone else', icon: 'arrowsClockwise', value: p?.first, sub: () => giveItems(id) },
      ...(waiting && swapItems(id).length ? [{ label: 'Swap with…', icon: 'arrowsLeftRight', sub: () => swapItems(id) }] : []),
      { label: 'Take it back', icon: 'arrowUUpLeft', danger: true, run: () => act.takeBackRoute(id) },
    );
  }
  return items;
}

/** The other routes waiting to start, with another courier: picking one exchanges the two routes' couriers. */
function swapItems(routeId: string): Item[] {
  const mm = m();
  const r = mm.route(routeId);
  return mm.routes
    .filter((x) => x.state === 'waiting' && x.id !== routeId && x.riderId !== r?.riderId)
    .map((x, i) => ({
      label: routeTitle(x, mm.zone),
      value: mm.person(x.riderId)?.first,
      avatar: av(mm.person(x.riderId)),
      key: i < 9 ? String(i + 1) : undefined,
      run: () => act.swapRoutes(routeId, x.id),
    }));
}

/** Routes' day picker: today, and each past day with routes in the last 60 days already read. */
function routesDays(): Item[] {
  const mm = m();
  const cur = ui.get().rday ?? mm.today;
  const n = new Map<string, number>();
  for (const r of mm.history ?? []) {
    const d = routeDay(r, mm.zone);
    if (d < mm.today) n.set(d, (n.get(d) ?? 0) + 1);
  }
  const days = [...n.keys()].sort().reverse();
  return [
    { hd: 'Routes on' },
    { label: 'Today', tick: cur === mm.today, run: () => ui.set({ rday: null }) },
    ...days.map((d) => ({ label: dayWords(d), value: plural(n.get(d)!, 'route'), tick: cur === d, run: () => ui.set({ rday: d }) })),
    ...(mm.history == null ? [{ note: 'Still reading the last 60 days' }] : days.length ? [] : [{ note: 'No routes in the last 60 days' }]),
  ];
}

function giveItems(routeId: string): Item[] {
  const mm = m();
  const r = mm.route(routeId);
  return mm.people.map((x, i) => ({
    label: x.first,
    value: x.day?.running ? `on a route, done about ${x.day.finishAt ? time(x.day.finishAt, mm.zone) : 'later'}` : x.day?.waiting.length
        ? 'a route waiting'
        : x.day?.finished[0]
          ? `${x.day.finished[0].state === 'ended' ? 'ended' : 'finished'} ${time(x.day.finished[0].completedAt ?? x.day.finished[0].endedAt, mm.zone)}`
          : 'not out yet',
    avatar: av(x),
    key: i < 9 ? String(i + 1) : undefined,
    tick: x.uid === r?.riderId,
    off: x.uid === r?.riderId && r?.state === 'waiting',
    run: () => act.giveTo(routeId, x.uid),
  }));
}

export function courierMenu(uid: string): Item[] {
  const mm = m();
  const p = mm.person(uid);
  if (!p) return [];
  const routeId = routeOfPerson(mm)(uid);
  const waiting = p.waitingHandIns[0];
  return [
    { hd: p.name },
    { label: 'Open the record', icon: 'user', key: '↵', run: () => openCourier(uid) },
    ...(routeId ? [{ label: 'Open the route', icon: 'path', key: 'R', run: () => openPerson(uid, routeOfPerson(mm)) }] : []),
    { label: `Plan a route for ${p.first}`, icon: 'paperPlaneTilt', key: 'P', run: () => planWith([], uid) },
    ...(() => {
      const theirs = mm.zones.filter((z) => z.couriers.includes(uid));
      return theirs.length
        ? [{ label: `${p.first}’s zones`, icon: 'polygon', value: theirs.map((z) => z.name).join(', '), run: () => openZones(theirs[0].id) }]
        : [{ label: `Draw a zone for ${p.first}`, icon: 'polygon', run: () => drawZone([uid]) }];
    })(),
    {
      label: `${p.first}’s stops today`,
      icon: 'mapPin',
      run: () => {
        go('stops');
        ui.set({ courier: uid, tab: 'all', sel: new Set() });
      },
    },
    ...call(`Call ${p.first}`, p.courier.phone),
    { label: 'Vehicle', icon: 'truck', value: vehicleWords(act.vehicleNow(p)), sub: () => vehicleItems(uid) },
    { label: 'On call or employed', icon: 'user', value: act.onCallNow(p) ? 'On call' : 'Employed', sub: () => payItems(uid) },
    ...(waiting ? [{ label: 'Confirm their hand-in', icon: 'money', value: undefined, run: () => openDialog({ kind: 'handIn', riderId: uid, handInId: waiting.id }) }] : []),
    ...(!waiting && p.balance > 0 ? [{ label: 'Record a hand-in', icon: 'money', run: () => openDialog({ kind: 'handIn', riderId: uid }) }] : []),
    ...(p.balance < 0 ? [{ label: `Record paying ${p.first}`, icon: 'money', run: () => openDialog({ kind: 'handIn', riderId: uid, out: true }) }] : []),
  ];
}

/** A courier's vehicle, from the phone's list (vehicle_type.dart). */
function vehicleItems(uid: string): Item[] {
  const p = m().person(uid);
  if (!p) return [];
  const cur = act.vehicleNow(p);
  return Object.entries(VEHICLES).map(([k, l]) => ({ label: l, tick: cur === k, run: () => act.changeVehicle(uid, k) }));
}

/** On call or employed (payment-at-the-door.html): what a courier keeps of the fees. */
function payItems(uid: string): Item[] {
  const p = m().person(uid);
  if (!p) return [];
  const on = act.onCallNow(p);
  return [
    { label: 'On call', tick: on, run: () => act.changeOnCall(uid, true) },
    { label: 'Employed', tick: !on, run: () => act.changeOnCall(uid, false) },
    { note: 'On call keeps the delivery fees; employed hands in everything taken.' },
  ];
}

// ── Sharing out (src/app/sharing.ts) ──────────────────────────────────

/** Couriers stops can be moved to while sharing out: everyone, those with a share first. */
function shareMoveItems(ids: string[]): Item[] {
  const mm = m();
  const view = sharedNow.get().view;
  const on = new Map((view?.routes ?? []).flatMap((r) => r.ids.map((id) => [id, r.uid] as [string, string])));
  const here = ids.length === 1 ? on.get(ids[0]) : undefined;
  const has = new Set((view?.routes ?? []).filter((r) => r.ids.length).map((r) => r.uid));
  const people = [...mm.people].sort((a, b) => Number(has.has(b.uid)) - Number(has.has(a.uid)));
  return people.map((p, i) => {
    const r = view?.routes.find((x) => x.uid === p.uid);
    return { label: p.first, value: r?.ids.length ? plural(r.ids.length, 'stop') : 'not in this share', avatar: av(p), key: i < 9 ? String(i + 1) : undefined, tick: here === p.uid, off: here === p.uid, run: () => act.moveShared(ids.filter((id) => on.get(id) !== p.uid), p.uid) };
  });
}

export function shareStopMenu(id: string): Item[] {
  const mm = m();
  const view = sharedNow.get().view;
  const w = mm.waiting.find((x) => x.id === id);
  if (!w || !view) return [];
  const sel = [...ui.get().sel].filter((k) => k.startsWith('share:')).map((k) => k.slice(6));
  if (sel.length > 1 && sel.includes(id)) return [{ hd: `${plural(sel.length, 'stop')} picked` }, { label: `Move ${plural(sel.length, 'stop')} to`, icon: 'arrowsClockwise', sub: () => shareMoveItems(sel), search: true, empty: () => [{ label: 'Show every courier', icon: 'x' }] }, { sep: true }, { label: 'Clear the picks', icon: 'x', run: () => ui.set({ sel: new Set() }) }];
  const left = view.left.find((x) => x.w.id === id);
  const everyone: Item[] = [{ label: 'Show every courier', icon: 'x' }];
  const items: Item[] = [{ hd: w.street }, { label: left ? 'Give it to' : 'Move to', icon: 'arrowsClockwise', key: 'M', sub: () => shareMoveItems([id]), search: true, empty: () => everyone }];
  if (left) {
    const { words, fixes } = leftWords(mm, left, ui.get().share?.couriers ?? []);
    items.push({ note: words }, ...fixes.map((f) => ({ label: f.label, icon: 'check', run: f.run })));
  }
  items.push({ sep: true }, { label: 'Open the waiting stop', icon: 'notePencil', run: () => openWaiting(id) }, { label: 'Copy the address', icon: 'copy', key: 'C', run: () => copyText(String(w.stop.writtenAddress ?? w.address), 'Address copied') });
  return items;
}

export function shareCourierMenu(uid: string): Item[] {
  const mm = m();
  const p = mm.person(uid);
  const share = ui.get().share;
  if (!p || !share) return [];
  const r = sharedNow.get().view?.routes.find((x) => x.uid === uid);
  const on = share.couriers.includes(uid);
  const most = mostOf(share, p);
  return [
    { hd: `${p.first}${r?.ids.length ? ` · ${plural(r.ids.length, 'stop')}` : ''}` },
    ...(r?.ids.length ? [{ label: `Send to ${p.first}`, icon: 'paperPlaneTilt', key: 'S', run: () => void act.sendShared([uid]) }] : []),
    ...(r?.ids.length ? [{ label: share.open === uid ? `Hide ${p.first}’s stops` : `Show ${p.first}’s stops`, icon: 'list', key: '↵', run: () => setShare((x) => ({ open: x.open === uid ? null : uid })) }] : []),
    { label: on ? `Leave ${p.first} out` : `Pick ${p.first}`, icon: on ? 'minus' : 'plus', run: () => setShare((x) => ({ couriers: on ? x.couriers.filter((u) => u !== uid) : [...x.couriers, uid] })) },
    {
      label: 'Most stops',
      icon: 'package',
      value: String(most),
      sub: () => [5, 10, 15, 20, 25, 30, 40, 50, 60].map((n) => ({ label: plural(n, 'stop'), tick: n === most, run: () => setShare((x) => ({ max: { ...x.max, [uid]: n } })) })),
    },
  ];
}

/** A route being planned: its runs and Discard, for ⌘K (Split into runs, as Plan a route offers it). */
export function planMenu(): Item[] {
  const mm = m();
  const plan = ui.get().plan;
  if (!plan) return [];
  const set = (next: Partial<act.Plan>) => ui.set({ plan: { ...plan, ...next } });
  const cap = act.capacityOf(mm, plan);
  const split = !!act.planRuns(mm, plan);
  const over = cap != null && plan.stops.length > 1 && loadOfAll(act.runStopsOf(plan)) > cap;
  return [
    ...(split
      ? [
          { label: 'Back to one run', icon: 'path', run: () => set({ runs: null }) },
          ...[2, 3, 4].map((n) => ({ label: `Split into ${n} runs`, icon: 'truck', tick: plan.runs?.n === n, run: () => set({ runs: { n } }) })),
        ]
      : over && act.planStart(mm, plan)
        ? [{ label: 'Split into runs', icon: 'truck', run: () => set({ runs: { n: null }, order: null, timed: null }) }]
        : []),
    { label: 'Discard the new route', icon: 'trash', danger: true, run: () => discardPlan((msg, undo) => toast(msg, { undo: () => void undo() })) },
  ];
}

/** The share's own actions, for ⌘K. */
export function shareMenu(): Item[] {
  const s = ui.get().share;
  if (!s) return [];
  const view = sharedNow.get().view;
  const n = view?.routes.filter((r) => r.ids.length).length ?? 0;
  return [
    ...(n ? [{ label: n > 1 ? `Send all ${plural(n, 'route')}` : 'Send the route', icon: 'paperPlaneTilt', run: () => void act.sendShared() }, { label: 'Sort all with Google', icon: 'path', run: () => void act.sortShared() }] : []),
    { label: 'Even stops', icon: 'scales', tick: s.balance === 'stops', run: () => setShare(() => ({ balance: 'stops' })) },
    { label: 'Even time', icon: 'clock', tick: s.balance === 'time', run: () => setShare(() => ({ balance: 'time' })) },
    { label: 'Fastest', icon: 'lightning', tick: s.balance === 'fastest', run: () => setShare(() => ({ balance: 'fastest' })) },
    { label: s.fewest ? 'The couriers I pick' : 'As few as needed', icon: s.fewest ? 'users' : 'usersThree', run: () => setShare((x) => ({ fewest: !x.fewest })) },
    { label: 'Discard the share', icon: 'trash', danger: true, run: () => discardShare((msg, undo) => toast(msg, { undo: () => void undo() })) },
  ];
}

// ── Zones (src/data/zones.ts) ─────────────────────────────────────────

export function zoneMenu(id: string): Item[] {
  const mm = m();
  const z = mm.zones.find((x) => x.id === id);
  if (!z) return [];
  const names = z.couriers.map((u) => mm.person(u)?.first).filter(Boolean);
  return [
    { hd: z.name },
    { label: 'Show on the map', icon: 'mapTrifold', key: '↵', run: () => openZones(id) },
    { label: 'Rename', icon: 'notePencil', key: 'R', run: () => (openZones(id), ui.set({ renaming: true })) },
    {
      label: 'Who covers it',
      icon: 'users',
      value: names.length ? (names.length === 1 ? names[0] : `${names.length} couriers`) : 'nobody yet',
      sub: () =>
        mm.people.length
          ? mm.people.map((p, i) => ({ label: p.first, avatar: av(p), key: i < 9 ? String(i + 1) : undefined, tick: z.couriers.includes(p.uid), run: () => act.toggleZoneCourier(id, p.uid) }))
          : [{ label: 'No couriers in your business yet', icon: 'users', off: true }, { label: 'Invite a courier', icon: 'userPlus', run: () => openDialog({ kind: 'invite' }) }],
    },
    { label: 'Change the shape', icon: 'polygon', key: 'S', run: () => (openZones(id), ui.set({ reshaping: true })) },
    { note: z.couriers.length ? 'Stops inside it go only to its couriers when stops are shared out.' : 'With no couriers it doesn’t limit who gets its stops.' },
    { sep: true },
    { label: 'Delete the zone', icon: 'trash', key: deleteKey, danger: true, run: () => act.deleteZone(id) },
  ];
}

export function lookMenu(id: string): Item[] {
  const mm = m();
  const x = mm.looks.find((l) => l.id === id);
  if (!x) return [];
  const items: Item[] = [{ hd: x.title }];
  const name = x.person?.first ?? 'them';
  if (x.stop) items.push({ label: 'Open the stop', icon: 'notePencil', key: '↵', run: () => openStop(keyOf(x.stop!), 'day') });
  else if (x.route) items.push({ label: 'Open the route', icon: 'path', key: '↵', run: () => openRoute(x.route!.id) });
  if (x.kind === 'late' && x.stop?.trackingCode) items.push({ label: 'Send the customer the link', icon: 'link', key: 'S', run: () => act.sendLink(x.stop!) });
  if ((x.kind === 'late' || x.kind === 'anotherDay') && x.stop?.phone) items.push(...call('Call the customer', x.stop.phone));
  if (x.kind === 'anotherDay' && x.stop) items.push({ label: 'Plan it on a new route', icon: 'calendarBlank', key: 'P', run: () => newRouteWith([x.stop!]) });
  if (x.kind === 'notAttempted' && x.route) items.push({ label: 'Send them on a new route', icon: 'path', key: 'P', run: () => newRouteWith(x.route!.stops.filter((s) => s.state === 'left')) });
  if (x.kind !== 'late' && x.kind !== 'anotherDay') items.push(...call(`Call ${name}`, x.person?.courier.phone));
  if (x.kind === 'stillOpen' && x.route) items.push({ label: `Remind ${name}`, icon: 'bell', run: () => act.remindCourier(x.route!.id) });
  if ((x.kind === 'declined' || x.kind === 'stale') && x.route) {
    items.push({ label: 'Give it to someone else', icon: 'arrowsClockwise', sub: () => giveItems(x.route!.id) }, { label: 'Take it back', icon: 'arrowUUpLeft', danger: true, run: () => act.takeBackRoute(x.route!.id) });
  }
  if ((x.kind === 'stopDeclined' || x.kind === 'stopStale') && x.id.includes(':')) {
    const sid = x.id.slice(x.id.indexOf(':') + 1);
    items.push({ label: 'Take it back', icon: 'arrowUUpLeft', danger: true, run: () => act.takeBackSentStop(sid) });
  }
  if (x.kind === 'handIn' && x.person) {
    const h = x.person.waitingHandIns.find((w) => `handIn:${w.id}` === x.id);
    if (h) items.push({ label: 'Confirm what came in', icon: 'check', key: '↵', run: () => openDialog({ kind: 'handIn', riderId: x.person!.uid, handInId: h.id }) });
  }
  if (x.clearable) {
    items.push(
      { sep: true },
      { label: 'Clear', icon: 'check', key: 'E', run: () => act.clearLook(x) },
      {
        label: 'Clear with a note',
        icon: 'notePencil',
        key: 'N',
        run: () => {
          if (ui.get().view !== 'day') backToDay();
          ui.set({ noting: x.id });
        },
      },
    );
  }
  return items;
}

const COLS: Record<string, string> = { addr: 'Address', status: 'Status', who: 'Customer', courier: 'Courier' };

export function colMenu(k: string): Item[] {
  const mm = m();
  const s = ui.get();
  const items: Item[] = [{ hd: COLS[k] ?? k }];
  items.push(
    { label: k === 'status' ? 'Earliest first' : 'Sort A to Z', icon: 'sortAscending', tick: s.sort?.k === k && s.sort.dir === 1, run: () => ui.set({ sort: { k, dir: 1 } }) },
    { label: k === 'status' ? 'Latest first' : 'Sort Z to A', icon: 'sortDescending', tick: s.sort?.k === k && s.sort.dir === -1, run: () => ui.set({ sort: { k, dir: -1 } }) },
  );
  if (s.sort) items.push({ label: 'Back to route order', icon: 'path', run: () => ui.set({ sort: null }) });
  if (k === 'courier') items.push({ label: 'Filter by courier', icon: 'funnelSimple', value: s.courier ? mm.person(s.courier)?.first : undefined, sub: () => courierFilter() });
  if (k === 'status')
    items.push({
      label: 'Filter by status',
      icon: 'funnelSimple',
      sub: () =>
        [
          ['all', 'All stops', 'mapPin'],
          ['waiting', 'Waiting to start', 'circleDashed'],
          ['togo', 'To go', 'arrowCircleRight'],
          ['done', 'Delivered', 'checkCircle'],
          ['failed', 'Not delivered', 'xCircle'],
        ].map(([t, l, i]) => ({ label: l, icon: i, tick: s.tab === t, run: () => ui.set({ tab: t, sel: new Set() }) })),
    });
  if (k !== 'addr') {
    items.push({
      sep: true,
    }, {
      label: 'Hide this column',
      icon: 'eyeSlash',
      run: () => ui.set((x) => ({ hideCols: new Set(x.hideCols).add(k) })),
    });
  }
  if (s.hideCols.size) items.push({ label: `Show ${plural(s.hideCols.size, 'hidden column')}`, icon: 'eye', run: () => ui.set({ hideCols: new Set() }) });
  return items;
}

function courierFilter(): Item[] {
  const mm = m();
  const s = ui.get();
  return mm.people
    .filter((p) => mm.stops.some((x) => x.riderId === p.uid))
    .map((p, i) => ({ label: p.first, avatar: av(p), key: i < 9 ? String(i + 1) : undefined, tick: s.courier === p.uid, run: () => ui.set({ courier: s.courier === p.uid ? null : p.uid, sel: new Set() }) }));
}

/** Stops filters beyond status and courier (control-room-web.html `addfilter`). */
export const FILTERS: { k: string; l: string; icon: string; f: (s: Stop) => boolean }[] = [
  { k: 'money', l: 'Money at the door', icon: 'money', f: (s) => toCollect(s) > 0 },
  { k: 'pickups', l: 'Pickups', icon: 'arrowUp', f: (s) => s.type === 'pickup' },
  { k: 'late', l: 'Late', icon: 'clock', f: (s) => (!handled(s) && s.eta != null && s.deliverBy != null && s.eta > s.deliverBy) || (s.state === 'done' && s.at != null && s.deliverBy != null && s.at > s.deliverBy) },
  { k: 'nophoto', l: 'No photo', icon: 'cameraSlash', f: (s) => s.state === 'done' && s.type === 'delivery' && !s.handover && !s.photos.length },
];

/** Stops grouped by route or courier, or not (board `routes-web`): kept for this computer. */
export function groupItems(): Item[] {
  const s = ui.get();
  return [
    { label: 'No groups', icon: 'list', tick: s.group === 'none', run: () => setGroup('none') },
    { label: 'By route', icon: 'path', tick: s.group === 'route', run: () => setGroup('route') },
    { label: 'By courier', icon: 'user', tick: s.group === 'courier', run: () => setGroup('courier') },
  ];
}

function addFilterMenu(): Item[] {
  const mm = m();
  const s = ui.get();
  return [
    { hd: 'Add filter' },
    { label: 'Courier', icon: 'users', value: s.courier ? mm.person(s.courier)?.first : undefined, sub: () => courierFilter() },
    ...FILTERS.map((f) => ({
      label: f.l,
      icon: f.icon,
      tick: s.flt.has(f.k),
      run: () =>
        ui.set((x) => {
          const flt = new Set(x.flt);
          if (flt.has(f.k)) flt.delete(f.k);
          else flt.add(f.k);
          return { flt, sel: new Set() };
        }),
    })),
  ];
}

export function bulkMenu(keys: string[]): Item[] {
  const mm = m();
  const stops = keys.map((k) => mm.stop(k)).filter((s): s is Stop => !!s);
  const ok = stops.filter((s) => changeable(mm, s.routeId)).map(keyOf);
  const links = stops.filter((s) => s.trackingCode && !s.handover);
  return [
    { hd: `${plural(stops.length, 'stop')} selected` },
    { label: `Move ${plural(ok.length, 'stop')} to`, icon: 'arrowsClockwise', off: !ok.length, sub: () => moveItems(ok) },
    ...(links.length ? [{ label: `Copy ${plural(links.length, 'tracking link')}`, icon: 'link', key: 'C', run: () => copyText(links.map((s) => `${streetOf(s)}: ${trackingLink(s.trackingCode!)}`).join('\n'), `${plural(links.length, 'tracking link')} copied`) }] : []),
    ...(ok.length ? [{ label: 'Plan a new route with them', icon: 'path', key: 'P', run: () => newRouteWith(ok.map((k) => mm.stop(k)!)) }] : []),
    ...(stops.length > ok.length ? [{ label: `${plural(stops.length - ok.length, 'stop')} on routes started or declined can’t move`, icon: 'check', off: true }] : []),
    { sep: true },
    { label: `Take off ${plural(ok.length, 'stop')}`, icon: 'trash', key: deleteKey, danger: true, off: !ok.length, run: () => act.takeOffStops(ok) },
  ];
}

export function spotMenu(lat: number, lng: number): Item[] {
  const at = { lat, lng };
  const label = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  return [
    { hd: `${lat.toFixed(4)}, ${lng.toFixed(4)}` },
    { label: 'Who’s closest to here', icon: 'crosshair', key: 'W', run: () => closestTo(at, label) },
    // Mid-plan, the pin joins the route being planned rather than starting another.
    { label: ui.get().plan?.stops.length ? 'Add to the new route' : 'Start a new route here', icon: 'plus', key: 'A', run: () => planWith([{ id: crypto.randomUUID(), address: `Dropped pin · ${label}`, area: '', lat, lng, pin: true }]) },
    { label: 'Copy the coordinates', icon: 'copy', key: 'C', run: () => copyText(`${lat.toFixed(6)}, ${lng.toFixed(6)}`, 'Coordinates copied') },
  ];
}

// ── Stops waiting for a route (docs/waiting-stops.md) ──────────────────

/** The days a waiting stop can be for, from the menu: the next week, no day, or another (a dialog). */
export function dayItems(ids: string[]): Item[] {
  const mm = m();
  const now = ids.length === 1 ? mm.waiting.find((w) => w.id === ids[0])?.day ?? null : undefined;
  const days = Array.from({ length: 7 }, (_, i) => addDays(mm.today, i));
  // Not past the day before the first of them goes (28 days after it was added): it'd be deleted before its day.
  const ends = mm.waiting.filter((w) => ids.includes(w.id) && w.expiresAt != null).map((w) => dayOf(w.expiresAt! - 864e5, mm.zone)).sort();
  const last = ends[0] ?? '9999-12-31';
  return [
    ...days.map((d, i) => ({ label: dayLabel(mm, d), icon: 'calendarBlank', key: i < 9 ? String(i + 1) : undefined, tick: now === d, off: d > last, run: () => act.setWaitingDay(ids, d) })),
    { label: 'Another day…', icon: 'calendarBlank', run: () => openDialog({ kind: 'waitingDay', ids }) },
    { sep: true },
    { label: 'No day', icon: 'minus', tick: now === null, run: () => act.setWaitingDay(ids, null) },
  ];
}

/**
 * Where waiting stops can go besides a new route: a route waiting to start, which takes them where they fit best; or a
 * courier on a route, sent them to fit in (a started route is theirs). Listed as Move to lists routes.
 */
export function addToItems(ids: string[]): Item[] {
  const mm = m();
  const waiting = mm.routes.filter((r) => r.state === 'waiting');
  const out: Item[] = waiting.map((r, i) => ({
    label: routeTitle(r, mm.zone),
    value: mm.person(r.riderId)?.first,
    avatar: av(mm.person(r.riderId)),
    key: i < 9 ? String(i + 1) : undefined,
    run: () => void act.addWaitingTo(ids, { routeId: r.id }),
  }));
  const out2: Item[] = mm.people
    .filter((p) => p.day?.running)
    .map((p) => ({ label: `Send to ${p.first}`, value: `on the ${routeTitle(p.day!.running!, mm.zone)}`, avatar: av(p), run: () => void act.addWaitingTo(ids, { riderId: p.uid }) }));
  if (out.length && out2.length) out.push({ sep: true });
  out.push(...out2);
  if (out2.length) out.push({ note: 'A started route is its courier’s: what’s sent shows on their map to fit in.' });
  if (!out.length) out.push({ label: 'No route is waiting to start, and nobody is on one', icon: 'path', off: true }, { label: ids.length === 1 ? 'Plan a route with it' : 'Plan a route with them', icon: 'path', run: () => void act.planFromWaiting(ids) });
  return out;
}

/** "Add to a route": the routes and couriers above, with a search when there are many; greyed, with why, when there are none. */
function addToItem(ids: string[]): Item[] {
  const mm = m();
  if (!mm.routes.some((r) => r.state === 'waiting') && !mm.people.some((p) => p.day?.running)) return [{ label: 'Add to a route', icon: 'plus', off: true }, { note: 'No route is waiting to start, and nobody is on one: plan a route instead.' }];
  return [
    {
      label: 'Add to a route',
      icon: 'plus',
      key: 'A',
      sub: () => addToItems(ids),
      search: true,
      empty: () => [{ label: 'Show every route', icon: 'x' }, { label: ids.length === 1 ? 'Plan a route with it' : 'Plan a route with them', icon: 'path', run: () => void act.planFromWaiting(ids) }],
    },
  ];
}

export function waitingMenu(id: string): Item[] {
  const mm = m();
  const w = mm.waiting.find((x) => x.id === id);
  if (!w) return [];
  return [
    { hd: w.street },
    { label: 'Open the stop', icon: 'notePencil', key: '↵', run: () => openWaiting(id) },
    { label: 'Plan a route with it', icon: 'path', key: 'P', run: () => void act.planFromWaiting([id]) },
    ...addToItem([id]),
    { label: 'Set the day', icon: 'calendarBlank', value: w.day ? dayLabel(mm, w.day) : undefined, sub: () => dayItems([id]) },
    { label: 'Copy the address', icon: 'copy', key: 'C', run: () => copyText(String(w.stop.writtenAddress ?? w.address), 'Address copied') },
    ...call(`Call ${w.name?.split(/\s+/)[0] ?? 'the customer'}`, w.phone),
    { note: fromLabel(w) },
    { sep: true },
    { label: 'Remove', icon: 'trash', key: deleteKey, danger: true, run: () => act.removeWaiting([id]) },
  ];
}

export function waitingBulkMenu(ids: string[]): Item[] {
  const mm = m();
  const ok = ids.filter((id) => mm.waiting.some((w) => w.id === id));
  return [
    { hd: `${plural(ok.length, 'stop')} selected` },
    { label: 'Plan a route with them', icon: 'path', key: 'P', run: () => void act.planFromWaiting(ok) },
    { label: 'Share out across couriers', icon: 'arrowsSplit', key: 'S', off: !mm.people.length, run: () => openShare(ok, mm.people.map((p) => p.uid)) },
    ...addToItem(ok),
    { label: 'Set the day', icon: 'calendarBlank', sub: () => dayItems(ok) },
    { sep: true },
    { label: `Remove ${plural(ok.length, 'stop')}`, icon: 'trash', key: deleteKey, danger: true, run: () => act.removeWaiting(ok) },
  ];
}

/** Filters for the waiting list: the day, and where they came from. */
function waitingDayFilter(): Item[] {
  const mm = m();
  const s = ui.get();
  const days = [...new Set(mm.waiting.map((w) => w.day).filter((d): d is string => !!d))].sort();
  const count = (f: (d: string | null) => boolean) => mm.waiting.filter((w) => f(w.day)).length;
  const pick = (v: string | null) => ui.set({ wday: s.wday === v ? null : v, sel: new Set() });
  return [
    { hd: 'Day' },
    ...days.map((d) => ({ label: dayLabel(mm, d), icon: 'calendarBlank', value: String(count((x) => x === d)), tick: s.wday === d, run: () => pick(d) })),
    { label: 'No day', icon: 'minus', value: String(count((x) => !x)), tick: s.wday === 'none', run: () => pick('none') },
    ...(s.wday ? [{ sep: true }, { label: 'Any day', icon: 'x', run: () => ui.set({ wday: null, sel: new Set() }) }] : []),
  ];
}

function waitingSourceFilter(): Item[] {
  const mm = m();
  const s = ui.get();
  const files = [...new Set(mm.waiting.map((w) => w.sheet?.file).filter((f): f is string => !!f))].sort();
  const pick = (v: string) => ui.set({ wsrc: s.wsrc === v ? null : v, sel: new Set() });
  return [
    { hd: 'Where they came from' },
    { label: 'Typed in', icon: 'notePencil', value: String(mm.waiting.filter((w) => w.source === 'typed').length), tick: s.wsrc === 'typed', run: () => pick('typed') },
    ...files.map((f) => ({ label: f, icon: 'table', value: String(mm.waiting.filter((w) => w.sheet?.file === f).length), tick: s.wsrc === `sheet:${f}`, run: () => pick(`sheet:${f}`) })),
    ...(s.wsrc ? [{ sep: true }, { label: 'From anywhere', icon: 'x', run: () => ui.set({ wsrc: null, sel: new Set() }) }] : []),
  ];
}

/** A new route holding [stops]: off a route not started, or sent again after it wasn't delivered. */
export function newRouteWith(stops: Stop[]) {
  const mm = m();
  const ps = stops.map((s) => planStopFrom(mm, s)).filter((x): x is act.PlanStop => !!x);
  if (!ps.length) return toast('Those stops have no position to send');
  planWith(ps);
}

export function showOnMap(key: string) {
  ui.set({ page: 'live', map: true, rec: null });
  openStop(key);
  frame();
}

function menuFor(key: string): Item[] {
  const i = key.indexOf(':');
  const type = i < 0 ? key : key.slice(0, i);
  const id = i < 0 ? '' : key.slice(i + 1);
  const s = ui.get();
  switch (type) {
    case 'stop': {
      const sel = [...s.sel];
      if (sel.length > 1 && sel.includes(id)) return bulkMenu(sel);
      return stopMenu(id);
    }
    case 'waiting': {
      const sel = [...s.sel];
      if (sel.length > 1 && sel.includes(id)) return waitingBulkMenu(sel);
      return waitingMenu(id);
    }
    case 'wsetday':
      return [{ hd: 'The day it’s for' }, ...dayItems(id.split(',').filter(Boolean))];
    case 'wadd':
      return [{ hd: 'Add to a route' }, ...addToItems(id.split(',').filter(Boolean))];
    case 'wday':
      return waitingDayFilter();
    case 'wsrc':
      return waitingSourceFilter();
    case 'route':
      return routeMenu(id);
    case 'give':
      return [{ hd: 'Who takes it' }, ...giveItems(id)];
    case 'rday':
      return routesDays();
    case 'zone':
      return zoneMenu(id);
    case 'sharestop':
      return shareStopMenu(id);
    case 'sharecourier':
      return shareCourierMenu(id);
    case 'sharemove':
      return [{ hd: 'Move to' }, ...shareMoveItems(id.split(',').filter(Boolean))];
    case 'courier':
      return courierMenu(id);
    case 'vehicle':
      return [{ hd: `${m().person(id)?.first ?? 'Their'}’s vehicle` }, ...vehicleItems(id)];
    case 'pay':
      return [{ hd: `${m().person(id)?.first ?? 'They'} is` }, ...payItems(id)];
    case 'look':
      return lookMenu(id);
    case 'col':
      return colMenu(id);
    case 'bulk':
      return bulkMenu([...s.sel]);
    case 'bulkmove':
      // The stops the bar showed (its key), not whatever else is still ticked out of sight.
      return [{ hd: 'Move to' }, ...moveItems(id.split(',').filter((k) => k && changeable(m(), k.slice(0, k.indexOf('/')))))];
    case 'move':
      return [{ hd: 'Move to' }, ...moveItems([id], id.slice(0, id.indexOf('/')))];
    case 'spot': {
      const [la, ln] = id.split(',').map(Number);
      return spotMenu(la, ln);
    }
    case 'give': {
      const r = m().route(id);
      return [{ hd: r ? `Give ${routeTitle(r, m().zone)} to` : 'Give it to' }, ...giveItems(id)];
    }
    case 'routes':
      return [{ hd: 'Today’s routes' }, ...m().routes.map((r, k) => ({ label: routeTitle(r, m().zone), value: m().person(r.riderId)?.first, avatar: av(m().person(r.riderId)), key: k < 9 ? String(k + 1) : undefined, tick: r.id === s.route, run: () => openRoute(r.id) }))];
    case 'addfilter':
      return addFilterMenu();
    case 'group':
      return [{ hd: 'Group stops' }, ...groupItems()];
    case 'settings':
    case 'me': {
      const mm = m();
      return [
        { hd: `${mm.ownerName ?? 'You'} · Owner of ${mm.business?.name ?? 'your business'}` },
        { label: s.theme === 'dark' ? 'Light' : 'Dark', icon: s.theme === 'dark' ? 'sun' : 'moon', run: theme },
        { label: 'Where routes start', icon: 'storefront', value: mm.place?.label, run: () => openDialog({ kind: 'startPlace' }) },
        { label: 'Settings', icon: 'gear', off: true },
        { note: 'Your business, payments, your plan and your account are in the Routr app’s Settings for now.' },
        { sep: true },
        // Anything still waiting its 5 s or being sent goes first: signed out, it couldn't.
        { label: 'Sign out', icon: 'signOut', run: () => void settle().then(() => signOut()) },
      ];
    }
    case 'invite':
      return [{ hd: 'Invite a courier' }, { label: 'Make an invite', icon: 'userPlus', key: '↵', run: () => openDialog({ kind: 'invite' }) }, { note: 'A link they open in the Routr app. It works once, for 7 days.' }];
  }
  return [];
}

/** Hooks the menus up: called once when the control room opens. */
export function useMenus() {
  menus(menuFor);
}

export { menuFor, closeMenu };
